import { STORAGE_BILLING_KEY } from '@/modules/billing/entities/billing.constants';
import { BillingModel } from '@/modules/billing/entities/billing.enums';
import { StorageBilling } from '@/modules/billing/entities/billing.interfaces';
import { BillingService } from '@/modules/billing/sevices/billingService';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import storage from '@/utils/storage/storage';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { useCallback, useEffect, useState } from 'react';

const { REACT_APP_BILLING_MODEL_DETECTION_ENABLED } = process.env;

export interface UseBillingModelProps {
  autoFetch?: boolean;
  initialBillingModel?: BillingModel;
}

export const useBillingModel = (
  options?: UseBillingModelProps,
) => {
  const {
    autoFetch = REACT_APP_BILLING_MODEL_DETECTION_ENABLED === 'true',
    initialBillingModel = BillingModel.FEE_FOR_LIFE,
  } = options || {};

  const { company } = useCompany();
  const [ billingModel, setBillingModel ] = useState<BillingModel>(initialBillingModel);
  const [loading, setLoading] = useState(true);

  const searchBilling = useCallback(async (companyId: string) => {
    return tryOrCatchMessageError(
      async () => {
        const storageBilling = storage.get<StorageBilling>(STORAGE_BILLING_KEY);
        if (storageBilling?.companyId === companyId && storageBilling?.model) {
          return {
            model: storageBilling.model,
          };
        }

        const { model } = await new BillingService().getBillingModel(companyId);
        storage.set(STORAGE_BILLING_KEY, {
          companyId,
          model,
        });

        return { model };
      },
      {
        messageErrorDefault: 'Não foi possível carregar as informações do modelo de faturamento, tente novamente mais tarde.',
        catchError: () => ({ model: BillingModel.FEE_FOR_LIFE }),
        tryBefore: () => setLoading(true),
        tryAfter: () => setLoading(false),
      },
    );
  }, []);

  useEffect(() => {
    if (!company?.id || !autoFetch) {
      return;
    }

    (async () => {
      const { model } = await searchBilling(company.id);
      setBillingModel(model);
    })();
  }, [ autoFetch, company?.id, searchBilling ]);

  return {
    searchBilling,
    companyBillingModel: billingModel,
    isCompanyFeeForService: billingModel === BillingModel.FEE_FOR_SERVICE,
    isCompanyFeeForLife: billingModel === BillingModel.FEE_FOR_LIFE,
    loading,
  };
};
